<template>
  <div>
    <v-autocomplete
      v-model="selectedProduct"
      hide-no-data
      ref="productSearchInput"
      @keyup.enter="searchIMEI"
      outlined
      :loading="productSearchLoading"
      prepend-inner-icon="search"
      :rounded="rounded"
      allow-overflow
      dense
      single-line
      :error-messages="error"
      :hide-details="error.length === 0"
      clearable
      eager
      id="productSearch"
      :items="Products"
      item-text="name"
      return-object
      :search-input.sync="phoneSearch"
      :label="$t('labels.searchProduct')"
      :items-per-page="20"
      :filter="customFilter"
    >
      <template v-slot:prepend-item>
        <v-list-item
          ripple
          @click="openMultipleProductsDialog"
          class="multi-open-item"
        >
          <v-list-item-icon>
            <v-icon color="primary">mdi-cart-plus</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="font-weight-bold">
              Choose Multiple Items
            </v-list-item-title>
            <v-list-item-subtitle>
              Add several non-serialized items at once
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="mb-1" />
      </template>

      <template v-slot:item="{ item }">
        <div class="d-flex justify-space-between align-center w-100">
          <div>
            <div v-html="$highlight(item.name, phoneSearch)"></div>
            <div class="grey--text text--darken-1 text-caption" v-if="item.sku">
              SKU: <span v-html="$highlight(item.sku, phoneSearch)"></span>
            </div>
            <small v-if="item.dynamicPrice === item.price" class="red--text">
              {{ item.dynamicPrice | currency }} *
            </small>
            <small v-else>
              {{ item.price | currency }}
              <v-icon small class="ml-1" v-if="currentUser.priceMarkupStatus"
                >mdi-sale</v-icon
              >
            </small>
          </div>

          <v-chip
            v-if="item.trackInventory"
            :color="item.trackSerialNumber ? 'light-primary' : 'teal darken-1'"
            dark
          >
            <v-avatar
              left
              class="light-blue darken-2"
              v-if="item.trackSerialNumber"
            >
              <v-icon small>mdi-barcode-scan</v-icon>
            </v-avatar>
            <v-avatar left v-else class="teal darken-3">
              <v-icon small>mdi-package-variant-closed</v-icon>
            </v-avatar>
            <span>{{ item.balance }}</span>
          </v-chip>
        </div>
      </template>
    </v-autocomplete>

    <v-dialog v-model="multipleProductsDialog" persistent max-width="720px">
      <v-card class="multi-product-card">
        <div class="dialog-header">
          <div>
            <div class="dialog-title">Add Multiple Items</div>
            <div class="dialog-subtitle">
              Search products, select quantities, then add them to cart.
            </div>
          </div>

          <v-btn icon small @click="closeMultipleProductsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="multi-search-bar">
          <v-text-field
            v-model="searchProduct"
            outlined
            prepend-inner-icon="mdi-magnify"
            dense
            label="Search products"
            single-line
            hide-details
            clearable
          />

          <v-chip small color="primary" text-color="white">
            {{ selectedProducts.length }} selected
          </v-chip>
        </div>

        <v-progress-linear
          v-if="multiProductLoading"
          indeterminate
          color="primary"
        />

        <div class="multi-result-info">
          <span v-if="multiProductLoading">Loading products...</span>
          <span v-else>{{ NonSerializedProducts.length }} products found</span>
        </div>

        <v-card-text class="multi-product-body">
          <v-list two-line class="multi-product-list">
            <template v-for="product in NonSerializedProducts">
              <v-list-item :key="product.id" class="multi-product-row">
                <v-list-item-action>
                  <v-checkbox
                    v-model="product.selected"
                    @change="productSelected(product)"
                    hide-details
                    color="primary"
                  />
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title class="product-name">
                    {{ product.name }}
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    <span v-if="product.sku">SKU: {{ product.sku }} · </span>
                    {{ product.price | currency }}
                    <span v-if="product.trackInventory">
                      · Stock: {{ product.balance }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action v-if="product.selected">
                  <div class="qty-control">
                    <v-btn
                      icon
                      small
                      color="red"
                      @click.stop.prevent="decrementQuantity(product)"
                    >
                      <v-icon small>mdi-minus</v-icon>
                    </v-btn>

                    <v-text-field
                      v-model.number="product.qty"
                      type="number"
                      dense
                      outlined
                      hide-details
                      class="qty-input"
                      @focus="$event.target.select()"
                    />

                    <v-btn
                      icon
                      small
                      color="primary"
                      @click.stop.prevent="incrementQuantity(product)"
                    >
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-action>
              </v-list-item>

              <v-divider :key="`divider-${product.id}`" />
            </template>
          </v-list>

          <div
            v-if="!multiProductLoading && !NonSerializedProducts.length"
            class="empty-products"
          >
            <v-icon size="44" color="grey">mdi-package-variant-closed</v-icon>
            <div class="empty-title">No products found</div>
            <div class="empty-subtitle">
              Try a different product name or SKU.
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog-actions">
          <div class="cart-summary">
            {{ cartProducts.length }} ready for cart
          </div>

          <v-spacer />

          <v-btn text color="primary" @click="closeMultipleProductsDialog">
            Close
          </v-btn>

          <v-btn
            color="primary"
            rounded
            dark
            :disabled="!cartProducts.length && !selectedProducts.length"
            @click="submitMultiple"
          >
            <v-icon left small>mdi-cart-plus</v-icon>
            Add
            {{ cartProducts.length || selectedProducts.length }}
            {{
              (cartProducts.length || selectedProducts.length) > 1
                ? "Items"
                : "Item"
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Products: {{ Products }} -->
  </div>
</template>

<script>
import productService from "@/modules/Product/service";
import { mapGetters, mapActions } from "vuex";
import purchaseService from "@/modules/Inventory/Purchase/service";
import PriceTierService from "@/modules/Product/PriceTier/service.js";
import _ from "lodash";
import CustomerPriceService from "@/modules/Customer/CustomerPrice/service.js";

export default {
  name: "product-autocomplete",
  props: {
    autofocus: {
      type: Boolean,
      default: true,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
    PriceTierId: {
      type: [Number, String],
      default: null,
    },
    nonSerialized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      model: null,
      PriceTiers: [],
      error: [],
      Products: [],
      selectedProduct: null,
      NonSerializedProducts: [],
      search: null,
      productSearchLoading: false,
      multipleProductsDialog: false,
      selectedProducts: [],
      phoneSearch: null,
      cartProducts: [],
      multiProductLoading: false,
      searchProduct: null,
    };
  },
  created() {
    if (
      !this.currentBusiness?.metadata?.isSingleCustomerPricing &&
      this.PriceTierId
    ) {
      return PriceTierService.getById(this.PriceTierId).then((response) => {
        this.loading = false;
        // console.log("PriceTierId response", response, val);
        this.PriceTiers = response.data;
        return response;
      });
    }
  },
  watch: {
    PriceTierId(val) {
      if (!this.currentBusiness?.metadata?.isSingleCustomerPricing && val) {
        return PriceTierService.getById(val).then((response) => {
          this.loading = false;
          // console.log("PriceTierId response", response);
          this.PriceTiers = response.data;
          return response;
        });
      }
    },

    async selectedProduct(val) {
      if (!val) return;

      // Work on a copy (optional, but avoids unintended mutation side-effects)
      const product = { ...val };

      // Apply tier-based pricing when NOT in single-customer pricing mode
      if (
        !this.currentBusiness?.metadata?.isSingleCustomerPricing &&
        this.PriceTiers
      ) {
        const tiers = this.PriceTiers;

        // Percentage discount
        if (tiers.discountType === "percentage" && tiers.rate) {
          const priceNum = Number(product.price) || 0;
          const rateNum = Number(tiers.rate) || 0;
          product.price = Number(
            (priceNum - (priceNum * rateNum) / 100).toFixed(2)
          );
        }

        // Fixed (per‑product) price
        if (
          tiers.discountType === "fixed" &&
          Array.isArray(tiers.ProductPrices)
        ) {
          const productPrice = tiers.ProductPrices.find(
            (p) => Number(p.ProductId) === Number(product.id)
          );
          if (productPrice && productPrice.price != null) {
            product.price = productPrice.price;
          }
        }
      }

      // Single-customer pricing path
      if (this.currentBusiness?.metadata?.isSingleCustomerPricing) {
        const customer = this.getSelectedCustomer;
        if (!customer || !customer.id) {
          // No selected customer — just submit what we have
          this.$emit("submit", product);
          return;
        }

        try {
          const response = await CustomerPriceService.getAll({
            ProductId: product.id,
            CustomerId: customer.id,
          });

          const rows = Array.isArray(response?.data) ? response.data : [];
          const cp = rows[0];

          if (cp && cp.price != null) {
            product.price = cp.price;
          }
          // If no custom price found, keep whatever price is already on product
        } catch (err) {
          // Log and fall back to the price we already computed
          console.error("CustomerPriceService error", err);
        }

        this.$emit("submit", product);
        return;
      }

      // Default emit (non single-customer path)
      this.$emit("submit", product);
    },
    phoneSearch(val) {
      if (val && val.length > 2) {
        this.getProduct();
      }
      // if (val && val.length > 1) this.error = [];
    },
    searchProduct: _.debounce(function () {
      this.fetchNonSerializedProducts();
    }, 300),
  },
  computed: {
    ...mapGetters("invoice", [
      "Invoice",
      "InvoiceDetails",
      "getSelectedCustomer",
    ]),
    ...mapGetters("global", ["currentUser", "currentBusiness"]),
  },
  methods: {
    ...mapActions("invoice", ["AddInvoiceDetail"]),
    ...mapActions("global", ["PlaySound"]),
    ...mapActions("layout", ["setOpenSerialSearch"]),
    async showUnavailableSerial(serial, imeiNumber) {
      const statusDetails = {
        sold: {
          title: "Serial Already Sold",
          status: "sold",
        },
        returned: {
          title: "Serial Returned to Vendor",
          status: "returned to the vendor",
        },
        lost: {
          title: "Serial Marked Lost",
          status: "marked as lost",
        },
        deleted: {
          title: "Serial Removed from Inventory",
          status: "removed from inventory",
        },
        repair: {
          title: "Serial Is Under Repair",
          status: "under repair",
        },
        onHold: {
          title: "Serial Is On Hold",
          status: "on hold",
        },
      };
      const detail = statusDetails[serial.status] || {
        title: "Serial Is Not Available",
        status: serial.status || "not available",
      };
      const hardActions = new Set([
        "purchase",
        "restock",
        "refundInvoice",
        "refund",
        "invoice",
        "markItSold",
        "returnToVendor",
        "invoiceDeleted",
        "deleted",
      ]);
      const latestAction = (serial.Log || [])
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .find((log) => hardActions.has(log.type));
      const actionText = latestAction
        ? ` Last inventory action: ${latestAction.message} on ${new Date(
            latestAction.createdAt
          ).toLocaleString()}.`
        : "";

      this.error = [
        `${imeiNumber} is ${detail.status} and cannot be sold.`,
      ];
      this.PlaySound("error");
      const result = await this.$swal({
        title: detail.title,
        text: `Serial ${imeiNumber} is currently ${detail.status}.${actionText} Open Serial Search to review its history and change or restock its status before reselling it.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Open Serial Search",
        cancelButtonText: "Close",
      });

      if (result.value) {
        this.setOpenSerialSearch({ imeiNumber });
      }
    },
    incrementQuantity(product) {
      product.qty++;
      if (product.qty > 100) product.qty = 100; // Limit to 100
    },
    decrementQuantity(product) {
      product.qty--;
      if (product.qty < 1) product.qty = 1; // Limit to minimum
    },
    // customFilter(item, queryText) {
    //   if (!queryText) return true;
    //   const terms = queryText.split(" ").filter(Boolean); // Split input by spaces
    //   const regex = new RegExp(
    //     terms.map((term) => `(?=.*${term})`).join(""),
    //     "i"
    //   ); // RegExp to match terms in any order
    //   return regex.test(item.name);
    // },
    customFilter(item, queryText) {
      if (!queryText) return true;

      const terms = queryText.split(" ").filter(Boolean);
      const regex = new RegExp(
        terms.map((term) => `(?=.*${term})`).join(""),
        "i"
      );

      const nameMatch = item.name && regex.test(item.name);
      const skuMatch = item.sku && regex.test(item.sku);

      return nameMatch || skuMatch;
    },
    emptyEntryBar() {
      this.$emit("emptyEntryBar");
      this.selectedProduct = null;
      this.phoneSearch = null;
      // console.log('empty entry bar call')
    },
    emptyAutoComplete() {
      // console.log('emptyAutoComplete')
      this.selectedProduct = null;
    },
    productSelected(product) {
      if (product.selected) {
        if (!this.selectedProducts.find((row) => row.id === product.id)) {
          this.selectedProducts.push(product);
        }
        return;
      }

      this.selectedProducts = this.selectedProducts.filter(
        (row) => row.id !== product.id
      );
    },
    // getProduct() {
    //   return productService.getAll({ search: this.phoneSearch, hideParent: true }).then((result) => {
    //     const product = result.data;
    //     this.Products = product
    //   });
    // },
    // getProduct() {
    //   return productService
    //     .getAll({ search: this.phoneSearch, hideParent: true })
    //     .then((result) => {
    //       console.log('product search result: ',this.phoneSearch, result)
    //       const product = result.data;
    //       if (this.currentUser.priceMarkupStatus) {
    //         product.forEach((row) => {
    //           const base = parseFloat(row.price) || 0;
    //           const pct = parseFloat(this.currentUser.priceMarkup) || 0;
    //           // newPrice = base + (base * pct / 100)
    //           const newPrice = base * (1 + pct / 100);
    //           // round to 2 decimals
    //           row.price = parseFloat(newPrice.toFixed(2));
    //         });
    //       }

    //       // Sort products based on the closeness to the search query
    //       this.Products = product.sort((a, b) => {
    //         const query = this.phoneSearch.toLowerCase();

    //         // Check if the name starts with the search query
    //         const aStartsWith = a.name.toLowerCase().startsWith(query) ? 0 : 1;
    //         const bStartsWith = b.name.toLowerCase().startsWith(query) ? 0 : 1;

    //         // Prioritize products whose name starts with the search query
    //         if (aStartsWith !== bStartsWith) {
    //           return aStartsWith - bStartsWith;
    //         }

    //         // If both start or neither starts, use indexOf to check proximity
    //         const aIndex = a.name.toLowerCase().indexOf(query);
    //         const bIndex = b.name.toLowerCase().indexOf(query);

    //         if (aIndex !== bIndex) {
    //           return aIndex - bIndex; // Closer to the start gets higher priority
    //         }

    //         // Finally, sort alphabetically if all else is equal

    //         return a.name.localeCompare(b.name);
    //       });
    //     });
    // },
    getProduct() {
      let where = {
        search: this.phoneSearch,
        hideParent: true,
      };

      if (this.nonSerialized) {
        where.inventoryType = "nonSerialize";
      }
      return productService.getAll(where).then((result) => {
        const product = result.data;
        if (this.currentUser.priceMarkupStatus) {
          product.forEach((row) => {
            const base = parseFloat(row.price) || 0;
            const pct = parseFloat(this.currentUser.priceMarkup) || 0;
            row.price = parseFloat((base * (1 + pct / 100)).toFixed(2));
          });
        }

        const query = this.phoneSearch.toLowerCase();
        this.Products = product
          .filter((item) => item.name || item.sku)
          .sort((a, b) => {
            const aMatch =
              a.name?.toLowerCase().startsWith(query) ||
              a.sku?.toLowerCase().startsWith(query);
            const bMatch =
              b.name?.toLowerCase().startsWith(query) ||
              b.sku?.toLowerCase().startsWith(query);
            if (aMatch && !bMatch) return -1;
            if (!aMatch && bMatch) return 1;
            return (a.name || "").localeCompare(b.name || "");
          });
      });
    },
    async fetchNonSerializedProducts() {
      this.multiProductLoading = true;

      const where = {
        hideParent: true,
        inventoryType: "nonSerialize",
        limit: 50,
      };

      if (this.searchProduct && this.searchProduct.length >= 3) {
        where.search = this.searchProduct;
      }

      try {
        const result = await productService.getAll(where);
        const products = result.data || [];

        this.NonSerializedProducts = products.map((row) => ({
          ...row,
          selected: false,
          qty: 1,
        }));

        return result;
      } finally {
        this.multiProductLoading = false;
      }
    },
    addToCart() {
      if (!this.selectedProducts.length) return false;
      this.selectedProducts.map((product) => {
        product.Bar = {
          name: product.name,
          qty: product.qty,
          price: product.price,
          boxQty: product.qty,
        };
        this.cartProducts.push(product);
      });
      this.selectedProducts = [];
      this.NonSerializedProducts.map((row) => {
        row.selected = false;
        row.qty = 1;
      });
    },
    submitMultiple() {
      const rows = this.selectedProducts.map((product) => ({
        ...product,
        Bar: {
          name: product.name,
          qty: Number(product.qty || 1),
          price: product.price,
          boxQty: Number(product.qty || 1),
        },
      }));

      if (!rows.length) return false;

      this.$emit("submitMultiple", rows);
      this.closeMultipleProductsDialog();
    },
    openMultipleProductsDialog() {
      this.multipleProductsDialog = true;
      this.fetchNonSerializedProducts();
    },
    closeMultipleProductsDialog() {
      (this.multipleProductsDialog = false),
        (this.cartProducts = []),
        (this.NonSerializedProducts = []),
        (this.selectedProducts = []),
        (this.searchProduct = null);
    },
    async searchIMEI() {
      this.error = [];
      if (!this.phoneSearch) return false;
      if (this.selectedProduct) return false;

      const searchValue = {
        imeiNumber: this.phoneSearch,
      };

      const imeiResult = this.InvoiceDetails.findIndex((row) => {
        // // console.log('searchValue.imeiNumber', searchValue.imeiNumber)
        // // console.log(
        //   "searchValue.imeiNumber",
        //   row,
        //   searchValue.imeiNumber.toUpperCase()
        // );
        if (row.imeiNumber) row.imeiNumber = row.imeiNumber.toUpperCase();
        if (searchValue.imeiNumber)
          searchValue.imeiNumber = searchValue.imeiNumber.toUpperCase();
        if (row.imeiNumber === searchValue.imeiNumber) {
          return true;
        }
      });
      // console.log("passed!", imeiResult); //355299070455844

      if (imeiResult !== -1) {
        if (this.InvoiceDetails[imeiResult].delete) {
          await this.UnDeleteInvoiceDetailRow(imeiResult);
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "success",
              title: "Item was restore",
            });

          this.emptyEntryBar();
          return false;
        }
        this.error.push(`${searchValue.imeiNumber} is already in this list`);
        this.productSearchLoading = false;
        this.PlaySound("error");
        // this.$swal
        //   .mixin({
        //     toast: true,
        //     position: "top-right",
        //     showConfirmButton: true,
        //     timer: 3000,
        //   })
        //   .fire({
        //     icon: "error",
        //     title: `${searchValue.imeiNumber} is already in this list`,
        //   });
        this.$swal({
          title: "Product Not Found",
          text: `${searchValue.imeiNumber} is already in this list`,
          icon: "warning",
        });
        this.emptyEntryBar();
      } else {
        // console.log('i am here')
        this.productSearchLoading = true;
        // console.log('searching for upc code', searchValue)
        searchValue.filteredStatus = ["repair", "onHold", "available"];
        return purchaseService
          .search(searchValue)
          .then(async (response) => {
            console.log("response imei number", response);
            if (response.data) {
              if (response.data.status === "repair") {
                this.error.push(
                  `${this.phoneSearch} is on repair please bring it on available before you can sell it`
                );
                this.productSearchLoading = false;
                this.PlaySound("error");
                this.$swal({
                  title: "Status On Repair",
                  text: `${this.phoneSearch} is on repair please bring it on available before you can sell it`,
                  icon: "warning",
                });
                return true;
              }
              if (response.data.status === "onHold") {
                this.error.push(
                  `${this.phoneSearch} is on hold please bring it on available before you can sell it`
                );
                this.productSearchLoading = false;
                this.PlaySound("error");
                this.$swal({
                  title: "Status On Hold",
                  text: `${this.phoneSearch} is on hold please bring it on available before you can sell it`,
                  icon: "warning",
                });
                return true;
              }
              this.productSearchLoading = false;
              let product = response.data;
              // product.Product = response.data.Product
              product.Product.imeiNumber = this.phoneSearch;
              // // console.log("response.data", response.data);
              // this.selectedProduct = response.data.Product;
              //     this.selectedProduct.Bar = {
              //   name: this.name,
              //   qty: this.qty,
              //   price: this.price,
              //   boxQty: this.boxQty,
              // };
              // console.log('product', product)

              this.AddInvoiceDetail({
                ProductId: product.Product.id,
                name: product.Product.name,
                price: product.Product.price,
                total: product.Product.price * 1,
                qty: 1,
                boxQty: 1,
                // unitCountInBox: product.Product.unitCountInBox,
                cost: product.cost | 0,
                imeiNumber: product.imeiNumber,
              });

              this.emptyEntryBar();
            } else {
              const unavailableSerial = await purchaseService.search({
                imeiNumber: this.phoneSearch,
                ignoreStatus: true,
              });

              if (unavailableSerial.data) {
                const searchedImei = this.phoneSearch;
                this.productSearchLoading = false;
                await this.showUnavailableSerial(
                  unavailableSerial.data,
                  searchedImei
                );
                return;
              }

              const upcProduct = (
                await productService.upcSearch({ upc: this.phoneSearch })
              ).data;

              if (upcProduct) {
                console.log("found upcProduct", upcProduct);
                let total = 0;
                // if (upcProduct.unitCountInBox && upcProduct.unitCountInBox > 0 && this.currentBusiness?.metadata?.boxCountOnInventory) {
                //   total = ((1 * upcProduct.unitCountInBox) * upcProduct.boxPrice).toFixed(2)
                //   total = parseFloat(total)
                // } else {
                //   total = upcProduct.price * 1
                // }
                if (this.currentBusiness?.metadata?.UPCbehave) {
                  total = upcProduct.price * upcProduct.unitCountInBox;
                  this.AddInvoiceDetail({
                    ProductId: upcProduct.id,
                    name: upcProduct.name,
                    price: upcProduct.price,
                    total: total,
                    qty: upcProduct.unitCountInBox,
                    boxQty: 1,
                    unitCountInBox: upcProduct.unitCountInBox,
                    cost: upcProduct.cost | 0,
                  });
                  this.emptyEntryBar();
                  this.productSearchLoading = false;
                } else {
                  // this.selectedProduct = upcProduct
                  this.productSearchLoading = false;
                  this.$emit("submit", upcProduct);
                  this.selectedProduct = upcProduct;
                  // this.$refs.qty.focus
                }
                // C75334982222
              } else {
                this.error.push(
                  `${this.phoneSearch} No Product with this search`
                );
                this.productSearchLoading = false;
                this.PlaySound("error");
                // this.$swal
                //   .mixin({
                //     toast: true,
                //     position: "top-right",
                //     showConfirmButton: true,
                //     timer: 3000,
                //   })
                //   .fire({
                //     icon: "error",
                //     title: `${this.phoneSearch} No Product with this serach`,
                //   });
                this.$swal({
                  title: "Product Not Found",
                  text: `${this.phoneSearch} No Product with this serach`,
                  icon: "warning",
                });
                this.emptyEntryBar();
              }
            }
          })
          .catch((error) => {
            this.productSearchLoading = false;
            this.error.push(`${this.phoneSearch} No Product with this search`);
            this.productSearchLoading = false;
            this.PlaySound("error");
            this.$swal({
              title: "Product Not Found",
              text: `${this.phoneSearch} No Product with this serach`,
              icon: "warning",
            });
            // this.$swal
            //   .mixin({
            //     toast: true,
            //     position: "top-right",
            //     showConfirmButton: true,
            //     timer: 3000,
            //   })
            //   .fire({
            //     icon: "error",
            //     title: `${this.phoneSearch} No Product with this serach`,
            //   });
            console.log("error", error);
          });
      }
    },
  },
};
</script>

<style scoped>
.multi-product-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.multi-search-bar {
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.multi-result-info {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.multi-product-body {
  max-height: 460px;
  overflow-y: auto;
  padding: 0 !important;
}

.multi-product-list {
  padding: 0;
}

.multi-product-row {
  min-height: 74px;
}

.product-name {
  font-weight: 800;
  color: #0f172a;
}

.qty-control {
  display: flex;
  align-items: center;
  gap: 6px;
}

.qty-input {
  width: 72px;
}

.qty-input input {
  text-align: center;
}

.dialog-actions {
  padding: 12px 16px;
  position: sticky;
  bottom: 0;
  background: #ffffff;
  z-index: 2;
}

.cart-summary {
  font-size: 13px;
  font-weight: 800;
  color: #64748b;
}

.empty-products {
  padding: 36px 16px;
  text-align: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 8px;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.multi-open-item {
  background: #f8fafc;
}

@media (max-width: 600px) {
  .multi-search-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .multi-product-body {
    max-height: 60vh;
  }

  .multi-product-row {
    align-items: flex-start;
  }

  .qty-control {
    margin-top: 8px;
  }

  .dialog-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .dialog-actions .v-btn {
    width: 100%;
  }
}
</style>
